import { apiFetch, parseErrorDetail } from "@/api/fetch";

export interface ClipboardFolder {
  name: string;
  doc_count: number;
  /** Full Clipboard-relative path on cross-folder command results. */
  path?: string;
  /** Newest activity of any file inside (recursive); ranks folders by recency. */
  latest_at?: string | null;
  search_rank?: number;
  search_match?: ClipboardSearchMatch;
  has_git?: boolean;
  shared?: boolean;
  owner_email?: string;
  owner_name?: string;
  owner_id?: string;
  role?: "viewer" | "editor";
}

export interface ClipboardShare {
  id: string;
  shared_with_id: string;
  shared_with_email: string;
  shared_with_name: string | null;
  role: "viewer" | "editor";
  created_at: string | null;
}

/** Indexing lifecycle for an uploaded doc; null/absent on legacy or linked docs. */
export type IndexStatus =
  | "pending"
  | "processing"
  | "awaiting_refresh"
  | "retry"
  | "ready"
  | "dead"
  | null;

export type ContentStatus =
  | "queued"
  | "quick_ready"
  | "processing"
  | "enhanced"
  | "partial"
  | "failed"
  | null;

export interface ClipboardDocument {
  id: string;
  title: string;
  filename: string;
  page_count: number | null;
  file_size_bytes: number | null;
  source_type: "pdf" | "artifact" | "bib" | "link" | "folder" | null;
  match_type: "fulltext" | "abstract" | "none" | null;
  created_at: string | null;
  updated_at?: string | null;
  index_status?: IndexStatus;
  content_status?: ContentStatus;
  link_type?: string | null;
  corpus_doc_id?: string | null;
  corpus_meta?: Record<string, unknown> | null;
  /** Present on cross-folder search results. */
  folder_path?: string;
  search_rank?: number;
  search_match?: ClipboardSearchMatch;
  /** Set when this row is a file shared with the current user by another owner. */
  owner_id?: string | null;
  shared?: boolean;
  shared_by_email?: string | null;
  role?: "viewer" | "editor" | null;
}

export interface ClipboardSearchMatch {
  score?: number | null;
  match_count?: number;
  snippet?: string;
  line_number?: number | null;
  section?: string | null;
}

/** True while a doc is still being parsed/indexed (searchability pending). */
export const isIndexPending = (s: IndexStatus): boolean =>
  s === "pending" || s === "processing" || s === "awaiting_refresh" || s === "retry";

export const isContentPending = (s: ContentStatus): boolean =>
  s === "queued" || s === "quick_ready" || s === "processing";

export interface ClipboardTree {
  path: string;
  folders: ClipboardFolder[];
  documents: ClipboardDocument[];
}

export type ClipboardAgentSearchStatus =
  | "pending"
  | "dispatched"
  | "running"
  | "executing"
  | "retry"
  | "complete"
  | "failed"
  | "cancelled";

export interface ClipboardAgentSearch {
  id: string;
  query: string;
  folder_scope: string;
  status: ClipboardAgentSearchStatus;
  error: string | null;
  result_count: number;
  operation?: "search" | "grep" | "ls" | null;
  planned_command?: string | null;
  execution_query?: string | null;
  execution_ms?: number | null;
  created_at?: string | null;
  completed_at?: string | null;
}

export interface ClipboardAgentSearchDetail extends ClipboardAgentSearch {
  documents: ClipboardDocument[];
  folders?: ClipboardFolder[];
}

export interface ClipboardStatus {
  total_documents: number;
  total_pages: number;
  total_bytes: number;
  folders: number;
  /** Per-user clipboard storage limit; absent on older servers. */
  storage_limit_bytes?: number;
  max_documents?: number;
  index_ready?: number;
  index_pending?: number;
  index_failed?: number;
  content_failed?: number;
}

export interface UploadResult {
  doc_id: string;
  short_id: string;
  title: string;
  /** Stored filename — may differ from the uploaded name (e.g. "paper (1).pdf"). */
  filename?: string;
  page_count: number | null;
  blocks: number;
  status: string;
  message?: string | null;
}

export interface ImportSummary {
  total: number;
  fulltext: number;
  abstract: number;
  citation_only: number;
  duplicates: number;
}

export interface DocumentContentLine {
  line_number: number;
  content: string;
  section: string | null;
}

export interface DocumentContent {
  document: ClipboardDocument & Record<string, unknown>;
  lines: DocumentContentLine[];
  total_lines: number;
  truncated: boolean;
  virtual_file?: {
    path: string;
    name: string;
    kind: "meta" | "lines" | "image" | "raw";
  };
  /** Client-side hint: open the preview in this view ("raw" = original file). */
  initial_view?: "parsed" | "raw";
}

export interface ClipboardDocumentFileEntry {
  name: string;
  kind: "file" | "folder";
  file_type: string;
  count?: number;
  preview_kind?: "image" | "raw";
  /** True when this entry is the user's original uploaded file. */
  original?: boolean;
}

export interface ClipboardDocumentFiles {
  document: ClipboardDocument & Record<string, unknown>;
  entries: ClipboardDocumentFileEntry[];
  sections: Array<{ name: string; line_count: number }>;
  figures: Array<{ name: string; page: number | null; original: boolean }>;
  total_lines: number;
}

export class ClipboardRequestError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ClipboardRequestError";
    this.status = status;
  }
}

const okJson = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    throw new ClipboardRequestError(await parseErrorDetail(response), response.status);
  }
  return response.json() as Promise<T>;
};

export const getTree = async (path: string, ownerId?: string): Promise<ClipboardTree> => {
  const params: Record<string, string> = { path };
  if (ownerId) params.owner_id = ownerId;
  const qs = new URLSearchParams(params).toString();
  const response = await apiFetch(`/user/tree?${qs}`, { method: "GET" });
  return okJson<ClipboardTree>(response);
};

export const createAgentSearch = async (
  query: string,
  folderScope: string,
): Promise<ClipboardAgentSearch> => {
  const response = await apiFetch("/user/agent-searches", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query, folder_scope: folderScope }),
  });
  return okJson<ClipboardAgentSearch>(response);
};

export const listAgentSearches = async (): Promise<ClipboardAgentSearch[]> => {
  const response = await apiFetch("/user/agent-searches?limit=12", { method: "GET" });
  const payload = await okJson<{ searches: ClipboardAgentSearch[] }>(response);
  return payload.searches;
};

export const getAgentSearch = async (
  searchId: string,
): Promise<ClipboardAgentSearchDetail> => {
  const response = await apiFetch(
    `/user/agent-searches/${encodeURIComponent(searchId)}`,
    { method: "GET" },
  );
  return okJson<ClipboardAgentSearchDetail>(response);
};

export const dismissAgentSearch = async (searchId: string): Promise<void> => {
  const response = await apiFetch(
    `/user/agent-searches/${encodeURIComponent(searchId)}`,
    { method: "DELETE" },
  );
  if (!response.ok && response.status !== 404) {
    throw new ClipboardRequestError(await parseErrorDetail(response), response.status);
  }
};

export const getRootFolders = async (): Promise<ClipboardFolder[]> => {
  const tree = await getTree("");
  return tree.folders;
};

export const normalizeClipboardFolderName = (raw: string): string =>
  raw.trim().replace(/\s+/g, "-").toLowerCase();

export const createClipboardFolder = async (name: string): Promise<void> => {
  const folder = normalizeClipboardFolderName(name);
  if (!folder) {
    throw new ClipboardRequestError("Folder name is required", 400);
  }
  const response = await apiFetch("/user/mcp/bash", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ command: `mkdir /clipboard/${folder}` }),
  });
  if (!response.ok) {
    throw new ClipboardRequestError(await parseErrorDetail(response), response.status);
  }
};

export const getStatus = async (): Promise<ClipboardStatus> => {
  const response = await apiFetch("/user/status", { method: "GET" });
  return okJson<ClipboardStatus>(response);
};

export const getDocumentContent = async (
  docId: string,
  maxLines = 400,
  ownerId?: string,
): Promise<DocumentContent> => {
  const params = new URLSearchParams({ max_lines: String(maxLines) });
  if (ownerId) params.set("owner_id", ownerId);
  const response = await apiFetch(
    `/user/documents/${encodeURIComponent(docId)}/content?${params}`,
    { method: "GET" },
  );
  return okJson<DocumentContent>(response);
};

export const getDocumentFiles = async (
  docId: string,
  ownerId?: string,
): Promise<ClipboardDocumentFiles> => {
  const params = new URLSearchParams();
  if (ownerId) params.set("owner_id", ownerId);
  const query = params.toString();
  const response = await apiFetch(
    `/user/documents/${encodeURIComponent(docId)}/files${query ? `?${query}` : ""}`,
    { method: "GET" },
  );
  return okJson<ClipboardDocumentFiles>(response);
};

export const getDocumentFigureBlob = async (
  docId: string,
  filename: string,
  ownerId?: string,
): Promise<Blob> => {
  const params = new URLSearchParams();
  if (ownerId) params.set("owner_id", ownerId);
  const query = params.toString();
  const response = await apiFetch(
    `/user/documents/${encodeURIComponent(docId)}/figures/${encodeURIComponent(filename)}${query ? `?${query}` : ""}`,
    { method: "GET" },
  );
  if (!response.ok) {
    throw new ClipboardRequestError(await parseErrorDetail(response), response.status);
  }
  return response.blob();
};

export interface UploadStep {
  step: number;
  total_steps: number;
  name: string;
  status: "running" | "done";
  detail: string;
}

interface UploadInitResponse {
  upload_id: string;
  upload_url: string;
  required_headers: Record<string, string>;
  max_file_size: number;
}

/**
 * Upload a file directly to GCS via a signed URL, then finalize.
 *
 * Bypasses Cloud Run's 32 MiB HTTP/1 request cap: the backend only sees two
 * small JSON calls (init + complete); the file bytes go straight to storage.
 */
/**
 * Legacy multipart upload through the backend. Used when the server can't
 * issue signed GCS URLs (local dev without cloud credentials, older servers).
 */
const uploadDocumentMultipart = async (
  file: File,
  folderPath: string,
): Promise<UploadResult> => {
  const form = new FormData();
  form.append("file", file);
  form.append("folder_path", folderPath);
  const response = await apiFetch("/user/upload", { method: "POST", body: form });
  return okJson<UploadResult>(response);
};

export const uploadDocumentDirect = async (
  file: File,
  folderPath: string,
  onPhase?: (phase: "uploading" | "processing") => void,
): Promise<UploadResult> => {
  onPhase?.("uploading");
  const initResponse = await apiFetch("/user/upload/init", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      filename: file.name,
      size_bytes: file.size,
      folder_path: folderPath,
      content_type: file.type || "application/octet-stream",
    }),
  });
  if ([404, 405, 501].includes(initResponse.status)) {
    const result = await uploadDocumentMultipart(file, folderPath);
    onPhase?.("processing");
    return result;
  }
  const init = await okJson<UploadInitResponse>(initResponse);

  const putResponse = await fetch(init.upload_url, {
    method: "PUT",
    headers: init.required_headers,
    body: file,
  });
  if (!putResponse.ok) {
    throw new ClipboardRequestError(
      `Storage upload failed (${putResponse.status})`,
      putResponse.status,
    );
  }

  onPhase?.("processing");
  const completeResponse = await apiFetch("/user/upload/complete", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      upload_id: init.upload_id,
      filename: file.name,
      folder_path: folderPath,
    }),
  });
  return okJson<UploadResult>(completeResponse);
};

export const uploadDocumentStream = async (
  file: File,
  folderPath: string,
  onStep: (step: UploadStep) => void,
): Promise<UploadResult> => {
  const send = (step: number, name: string, status: "running" | "done", detail = "") =>
    onStep({ step, total_steps: 3, name, status, detail });

  // Steps are synthesized client-side; the transfer goes straight to GCS,
  // so there is no backend SSE stream to relay anymore.
  const result = await uploadDocumentDirect(file, folderPath, (phase) => {
    if (phase === "uploading") {
      send(1, "Uploading", "running");
    } else {
      send(1, "Uploading", "done");
      send(2, "Adding", "running", "extracting quick local text");
    }
  });
  send(2, "Adding", "done", `${result.blocks ?? 0} quick-text blocks`);
  send(3, "Added", "done", "rich processing continues in the background");
  return result;
};

export const importBibliography = async (
  file: File,
  folderPath: string,
): Promise<{ folder: string; summary: ImportSummary }> => {
  const form = new FormData();
  form.append("file", file);
  form.append("folder_path", folderPath);
  const response = await apiFetch("/user/import", {
    method: "POST",
    body: form,
  });
  return okJson<{ folder: string; summary: ImportSummary }>(response);
};

/**
 * Trigger a browser download of a document's original uploaded file. The backend
 * streams the bytes as an attachment (same origin via the /api proxy, so no CORS
 * and the filename comes from Content-Disposition). Corpus links have no
 * original file (404).
 */
export const downloadDocument = async (docId: string, filename?: string): Promise<void> => {
  const response = await apiFetch(`/user/documents/${encodeURIComponent(docId)}/download`);
  if (!response.ok) {
    throw new ClipboardRequestError(await parseErrorDetail(response), response.status);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "document";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

/**
 * Fetch the original uploaded file's bytes (for inline "raw" preview). Returned
 * as a Blob so the caller can make a same-origin object URL to embed. Throws a
 * ClipboardRequestError (status 404) when there's no original file yet.
 */
export const getDocumentRawBlob = async (docId: string): Promise<Blob> => {
  const response = await apiFetch(`/user/documents/${encodeURIComponent(docId)}/download`);
  if (!response.ok) {
    throw new ClipboardRequestError(await parseErrorDetail(response), response.status);
  }
  return response.blob();
};

/**
 * Download a ZIP of original files. If `ids` are given, exports exactly those
 * documents; otherwise the folder (empty path = whole clipboard).
 */
export const exportClipboard = async (folderPath: string, ids?: string[]): Promise<void> => {
  const search = new URLSearchParams();
  if (ids && ids.length > 0) search.set("ids", ids.join(","));
  else if (folderPath) search.set("folder_path", folderPath);
  const params = search.toString() ? `?${search.toString()}` : "";
  const response = await apiFetch(`/user/export${params}`);
  if (!response.ok) {
    throw new ClipboardRequestError(await parseErrorDetail(response), response.status);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `clipboard-${folderPath || "all"}.zip`.replace(/\//g, "-");
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const deleteDocument = async (docId: string): Promise<void> => {
  const response = await apiFetch(`/user/documents/${encodeURIComponent(docId)}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    throw new ClipboardRequestError(await parseErrorDetail(response), response.status);
  }
};

export const deleteFolder = async (folderPath: string): Promise<void> => {
  const response = await apiFetch(`/user/folders/${encodeURIComponent(folderPath)}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    throw new ClipboardRequestError(await parseErrorDetail(response), response.status);
  }
};

// ── Add from Paperclip (corpus link) ──

export interface PaperSearchResult {
  id: string;
  title: string;
  authors?: string;
  source?: string;
  year?: string;
}

export const searchPapers = async (query: string): Promise<PaperSearchResult[]> => {
  const response = await apiFetch("/cli/execute", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ command: "search", raw: `--json -n 10 ${query}` }),
  });
  if (!response.ok) return [];
  const data = await response.json();
  const resultData = data.result_data;
  if (!resultData?.papers) return [];
  return resultData.papers.map((p: Record<string, unknown>) => ({
    id: p.document_id || p.pmc_id || p.paper_id || p.id || p.source_key || "",
    title: p.title || "Untitled",
    authors: p.authors as string | undefined,
    source: p.source as string | undefined,
    year: (p.year || p.pub_year) as string | undefined,
  }));
};

export const addCorpusLink = async (paperId: string, folderPath: string): Promise<string> => {
  const dest = folderPath ? `/clipboard/${folderPath}/` : "/clipboard/";
  const response = await apiFetch("/user/mcp/bash", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ command: `cp /papers/${paperId} ${dest}` }),
  });
  if (!response.ok) throw new ClipboardRequestError(await parseErrorDetail(response), response.status);
  const data = await response.json();
  return data.message || "Linked";
};

// ── HTML Preview ──

export const getDocumentHtml = async (docId: string, ownerId?: string): Promise<{ html: string; doc_id: string } | null> => {
  const params = new URLSearchParams();
  if (ownerId) params.set("owner_id", ownerId);
  const qs = params.toString();
  const response = await apiFetch(`/user/documents/${encodeURIComponent(docId)}/html${qs ? `?${qs}` : ""}`);
  if (response.status === 404) return null;
  if (!response.ok) throw new ClipboardRequestError(await parseErrorDetail(response), response.status);
  return response.json();
};

// ── Corpus figures (for corpus-link previews) ──

export interface PaperFigure {
  filename: string;
  figure_num: number | null;
  table_num?: number | null;
  /** 1-based content.lines number of the caption, when it could be located. */
  line_num: number | null;
  /** Backend-relative image URL, e.g. "/api/papers/<id>/figures/<file>". */
  url: string;
}

/**
 * Fetch extracted figure/table images for a corpus document (PMC, arXiv,
 * bioRxiv/medRxiv). Returns [] on any error so the preview degrades to
 * text-only rather than failing. The image URLs are unauthenticated and can be
 * used directly as <img src> (made absolute for iframe srcdoc rendering).
 */
export const getPaperFigures = async (corpusDocId: string): Promise<PaperFigure[]> => {
  try {
    const response = await apiFetch(
      `/papers/${encodeURIComponent(corpusDocId)}/figures`,
      { method: "GET" },
    );
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data?.figures) ? (data.figures as PaperFigure[]) : [];
  } catch {
    return [];
  }
};

// ── Sharing ──

export const listShares = async (folderPath: string): Promise<ClipboardShare[]> => {
  const response = await apiFetch(`/user/shares?folder_path=${encodeURIComponent(folderPath)}`);
  if (!response.ok) throw new ClipboardRequestError(await parseErrorDetail(response), response.status);
  return response.json();
};

export const createShare = async (folderPath: string, email: string, role: "viewer" | "editor"): Promise<ClipboardShare> => {
  const response = await apiFetch("/user/shares", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ folder_path: folderPath, email, role }),
  });
  if (!response.ok) throw new ClipboardRequestError(await parseErrorDetail(response), response.status);
  return response.json();
};

export const listDocumentShares = async (documentId: string): Promise<ClipboardShare[]> => {
  const response = await apiFetch(`/user/shares?document_id=${encodeURIComponent(documentId)}`);
  if (!response.ok) throw new ClipboardRequestError(await parseErrorDetail(response), response.status);
  return response.json();
};

export const shareDocument = async (documentId: string, email: string, role: "viewer" | "editor"): Promise<ClipboardShare> => {
  const response = await apiFetch("/user/shares", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ document_id: documentId, email, role }),
  });
  if (!response.ok) throw new ClipboardRequestError(await parseErrorDetail(response), response.status);
  return response.json();
};

export const revokeShare = async (shareId: string): Promise<void> => {
  const response = await apiFetch(`/user/shares/${shareId}`, { method: "DELETE" });
  if (!response.ok) throw new ClipboardRequestError(await parseErrorDetail(response), response.status);
};

export const updateShareRole = async (shareId: string, role: "viewer" | "editor"): Promise<void> => {
  const response = await apiFetch(`/user/shares/${shareId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ role }),
  });
  if (!response.ok) throw new ClipboardRequestError(await parseErrorDetail(response), response.status);
};
